<template>
  <v-container max-width="100%" style="margin-top: 2%">
    <v-row align="center" class="mb-4">
      <v-col cols="12" sm="6" md="4">
        <span class="headline" style="color: #4a4a4a">{{ $t('routines.title') }}</span>
      </v-col>
      <v-col cols="12" sm="6" md="8" class="d-flex align-center justify-end flex-wrap">
        <v-switch
          v-model="autoRefresh"
          :label="$t('routines.autoRefresh')"
          hide-details
          class="mr-4 mt-0"
          @change="toggleAutoRefresh"
        />
        <v-btn color="primary" fab small dark @click="loadRotinas" :loading="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="loading && rotinas.length === 0" justify="center">
      <v-col cols="12" class="text-center py-8">
        <v-progress-circular indeterminate color="primary" />
      </v-col>
    </v-row>

    <v-row v-else-if="!loading && rotinas.length === 0" justify="center">
      <v-col cols="12" class="text-center py-8 grey--text">
        {{ $t('routines.empty') }}
      </v-col>
    </v-row>

    <v-row v-else align="stretch">
      <v-col
        v-for="rotina in rotinas"
        :key="rotina.id_rotina"
        cols="12"
        md="6"
        lg="4"
        class="d-flex"
      >
        <v-card class="flex-grow-1 d-flex flex-column" style="width: 100%">
          <v-card-title class="pb-2">
            <div class="d-flex align-center justify-space-between" style="width: 100%">
              <span class="subtitle-1 font-weight-bold">{{ rotina.nome }}</span>
              <v-chip small :color="statusColor(rotina.status)" dark>
                {{ statusLabel(rotina.status) }}
              </v-chip>
            </div>
          </v-card-title>

          <v-card-text class="flex-grow-1 pt-0">
            <p class="body-2 grey--text text--darken-1 mb-3">{{ rotina.descricao || '—' }}</p>

            <v-list dense class="py-0">
              <v-list-item class="px-0">
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>{{ $t('routines.fields.type') }}:</strong>
                    <span class="ml-1">{{ typeLabel(rotina) }}</span>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item class="px-0">
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>{{ scheduleLabel(rotina) }}:</strong>
                    <span class="ml-1">{{ scheduleValue(rotina) }}</span>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item class="px-0">
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>{{ $t('routines.fields.lastUpdate') }}:</strong>
                    <span class="ml-1">{{ formatDateTime(rotina.dt_ultima_atualizacao) }}</span>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <v-divider class="my-3" />

            <div class="d-flex align-center">
              <v-switch
                :input-value="isLigada(rotina)"
                :label="$t('routines.fields.power')"
                hide-details
                class="mt-0"
                :disabled="!canToggleLigada || savingLigadaId === rotina.id_rotina"
                @change="onLigadaChange(rotina, $event)"
              />
            </div>
          </v-card-text>

          <v-card-actions class="pt-0">
            <v-spacer />
            <v-tooltip bottom v-if="canConfigure">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="openConfigDialog(rotina)"
                >
                  <v-icon>mdi-cog</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('routines.actions.configure') }}</span>
            </v-tooltip>
            <v-tooltip bottom v-if="canReset">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="orange darken-2"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="rotina.status === 1 || resettingId === rotina.id_rotina"
                  @click="openResetDialog(rotina)"
                >
                  <v-icon>mdi-restart</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('routines.actions.reset') }}</span>
            </v-tooltip>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="configDialog" max-width="480" persistent>
      <v-card>
        <v-card-title>
          <span class="headline" style="color: #4a4a4a">{{ $t('routines.configDialog.title') }}</span>
        </v-card-title>
        <v-card-subtitle v-if="configRotina">{{ configRotina.nome }}</v-card-subtitle>
        <v-card-text>
          <v-form ref="configForm" v-model="configFormValid">
            <v-text-field
              v-if="configRotina && isCiclica(configRotina)"
              v-model.number="configSegundos"
              type="number"
              min="1"
              :label="$t('routines.fields.intervalSeconds')"
              :rules="[(v) => Number(v) > 0 || $t('routines.validation.invalidInterval')]"
            />
            <v-text-field
              v-if="configRotina && !isCiclica(configRotina)"
              v-model="configHorario"
              :label="$t('routines.fields.scheduledTime')"
              placeholder="HH:MM:SS"
              :rules="[(v) => !!v && /^\d{2}:\d{2}(:\d{2})?$/.test(v) || $t('routines.validation.invalidTime')]"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeConfigDialog">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="primary" dark :loading="savingConfig" @click="saveConfig">{{ $t('button.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmDialog" persistent max-width="500">
      <v-card>
        <v-card-title class="headline">
          <span style="color: #d60000">{{ $t('system.alert') }}</span>
        </v-card-title>
        <v-card-text>
          <span>{{ confirmMessage }}</span>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmDialog = false">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="primary" dark :loading="confirmLoading" @click="confirmAction">{{ $t('button.confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';

export default {
  name: 'Rotinas',
  data() {
    return {
      user: null,
      recursos: [],
      rotinas: [],
      loading: false,
      autoRefresh: true,
      refreshInterval: null,
      savingLigadaId: null,
      resettingId: null,
      configDialog: false,
      configRotina: null,
      configSegundos: null,
      configHorario: '',
      configFormValid: false,
      savingConfig: false,
      confirmDialog: false,
      confirmMessage: '',
      confirmLoading: false,
      pendingResetRotina: null,
    };
  },
  computed: {
    canToggleLigada() {
      if (this.user && this.user.is_superadmin === 1) return true;
      return this.recursos.some((r) => r.rota === '/api/v1.0/updaterotinaligada');
    },
    canConfigure() {
      if (this.user && this.user.is_superadmin === 1) return true;
      return this.recursos.some((r) => r.rota === '/api/v1.0/updaterotinaparams');
    },
    canReset() {
      if (this.user && this.user.is_superadmin === 1) return true;
      return this.recursos.some((r) => r.rota === '/api/v1.0/resetrotina');
    },
  },
  created() {
    this.user = JSON.parse(sessionStorage.getItem('user'));
    this.populaRecursos();
  },
  mounted() {
    this.toggleAutoRefresh();
  },
  beforeDestroy() {
    this.clearAutoRefresh();
  },
  methods: {
    populaRecursos() {
      if (this.user && this.user.is_superadmin === 1) {
        this.recursos = [
          { rota: '/api/v1.0/updaterotinaligada' },
          { rota: '/api/v1.0/updaterotinaparams' },
          { rota: '/api/v1.0/resetrotina' },
        ];
        this.loadRotinas();
        return;
      }
      apiController.request(apiController.endpoints.getRecursosusuario, {
        params: { id: this.user.id_usuario },
        onSuccess: (response) => {
          this.recursos = response.data || [];
          this.loadRotinas();
        },
        onError: (error) => {
          EventBus.$emit('show-error', { error });
        },
      });
    },
    loadRotinas() {
      this.loading = true;
      apiController.request(apiController.endpoints.getallRotinas, {
        onSuccess: (response) => {
          this.rotinas = response.data || [];
          this.loading = false;
        },
        onError: (error) => {
          this.loading = false;
          EventBus.$emit('show-error', { error });
        },
      });
    },
    toggleAutoRefresh() {
      this.clearAutoRefresh();
      if (this.autoRefresh) {
        this.refreshInterval = setInterval(() => {
          this.loadRotinas();
        }, 30000);
      }
    },
    clearAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
    isLigada(rotina) {
      return rotina.ligada === 1 || rotina.ligada === true;
    },
    isCiclica(rotina) {
      return parseInt(rotina.ciclo, 10) === 1;
    },
    statusColor(status) {
      if (status === 1) return 'green darken-1';
      if (status === 2) return 'red darken-1';
      return 'grey';
    },
    statusLabel(status) {
      if (status === 1) return this.$t('routines.status.running');
      if (status === 2) return this.$t('routines.status.error');
      return this.$t('routines.status.stopped');
    },
    typeLabel(rotina) {
      return this.isCiclica(rotina)
        ? this.$t('routines.type.cyclic')
        : this.$t('routines.type.scheduled');
    },
    scheduleLabel(rotina) {
      return this.isCiclica(rotina)
        ? this.$t('routines.fields.interval')
        : this.$t('routines.fields.scheduledTime');
    },
    scheduleValue(rotina) {
      if (this.isCiclica(rotina)) {
        return this.formatInterval(rotina.segundos_ciclo);
      }
      return this.formatTime(rotina.horario_programado);
    },
    formatInterval(seconds) {
      var sec = parseInt(seconds, 10);
      if (isNaN(sec) || sec < 1) return '—';
      if (sec < 60) return this.$t('routines.interval.seconds', { count: sec });
      if (sec % 3600 === 0) {
        return this.$t('routines.interval.hours', { count: sec / 3600 });
      }
      if (sec % 60 === 0) {
        return this.$t('routines.interval.minutes', { count: sec / 60 });
      }
      return this.$t('routines.interval.seconds', { count: sec });
    },
    formatTime(value) {
      if (!value) return '—';
      if (typeof value === 'string') {
        return value.length >= 5 ? value.substring(0, 8) : value;
      }
      return String(value);
    },
    formatDateTime(value) {
      if (!value) return '—';
      var date = new Date(value);
      if (isNaN(date.getTime())) return String(value);
      return date.toLocaleString();
    },
    onLigadaChange(rotina, value) {
      if (!this.canToggleLigada) return;
      this.savingLigadaId = rotina.id_rotina;
      apiController.request(apiController.endpoints.updateRotinaligada, {
        data: {
          id_rotina: rotina.id_rotina,
          ligada: value ? 1 : 0,
        },
        onSuccess: () => {
          rotina.ligada = value ? 1 : 0;
          this.savingLigadaId = null;
          this.loadRotinas();
        },
        onError: (error) => {
          this.savingLigadaId = null;
          EventBus.$emit('show-error', { error });
          this.loadRotinas();
        },
      });
    },
    openConfigDialog(rotina) {
      this.configRotina = rotina;
      this.configSegundos = rotina.segundos_ciclo;
      this.configHorario = this.formatTime(rotina.horario_programado);
      this.configDialog = true;
      this.$nextTick(() => {
        if (this.$refs.configForm) this.$refs.configForm.resetValidation();
      });
    },
    closeConfigDialog() {
      this.configDialog = false;
      this.configRotina = null;
    },
    saveConfig() {
      if (!this.$refs.configForm || !this.$refs.configForm.validate()) return;
      if (!this.configRotina) return;

      var payload = { id_rotina: this.configRotina.id_rotina };
      if (this.isCiclica(this.configRotina)) {
        payload.segundos_ciclo = this.configSegundos;
      } else {
        payload.horario_programado = this.configHorario;
      }

      this.savingConfig = true;
      apiController.request(apiController.endpoints.updateRotinaparams, {
        data: payload,
        onSuccess: () => {
          this.savingConfig = false;
          this.closeConfigDialog();
          this.loadRotinas();
        },
        onError: (error) => {
          this.savingConfig = false;
          EventBus.$emit('show-error', { error });
        },
      });
    },
    openResetDialog(rotina) {
      this.pendingResetRotina = rotina;
      this.confirmMessage = this.$t('routines.confirmReset', { name: rotina.nome });
      this.confirmDialog = true;
    },
    confirmAction() {
      if (!this.pendingResetRotina) {
        this.confirmDialog = false;
        return;
      }
      var rotina = this.pendingResetRotina;
      this.confirmLoading = true;
      this.resettingId = rotina.id_rotina;
      apiController.request(apiController.endpoints.resetrotina, {
        data: { id_rotina: rotina.id_rotina },
        onSuccess: () => {
          this.confirmLoading = false;
          this.confirmDialog = false;
          this.pendingResetRotina = null;
          this.resettingId = null;
          this.loadRotinas();
        },
        onError: (error) => {
          this.confirmLoading = false;
          this.resettingId = null;
          EventBus.$emit('show-error', { error });
        },
      });
    },
  },
};
</script>
